import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CalendarBlank, Clock } from "@phosphor-icons/react";
import SEO from "../components/SEO";
import guides from "../content/guides.json";

const SITE_URL = "https://competitionchecker.com";
const PUBLISHER_NAME = "Competition Checker editorial team";

function GuideCard({ guide }) {
  return (
    <article className="rounded-lg border border-white/10 bg-white/[0.03] p-6">
      <div className="flex flex-wrap gap-x-4 gap-y-2 text-[11px] text-white/50">
        <span className="inline-flex items-center gap-1.5">
          <Clock size={13} /> {guide.reading_time}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <CalendarBlank size={13} /> Reviewed {guide.reviewed}
        </span>
      </div>
      <h2 className="mt-4 font-display text-xl font-semibold text-white">
        <Link className="hover:text-[var(--cc-gold)]" to={`/guides/${guide.slug}`}>
          {guide.title}
        </Link>
      </h2>
      <p className="mt-3 text-sm leading-relaxed text-[var(--cc-text-muted)]">
        {guide.summary}
      </p>
      <Link
        className="mt-5 inline-flex items-center gap-1.5 text-sm text-[var(--cc-gold)] hover:underline"
        to={`/guides/${guide.slug}`}
      >
        Read guide <ArrowRight size={14} />
      </Link>
    </article>
  );
}

export function GuidesList() {
  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-8 py-14">
      <SEO
        title="Guides to UK prize competition odds and transparency"
        description="Practical, independent guides to UK prize competitions, ticket caps, maximum-pool odds, free postal entry and fixed-budget comparisons."
        path="/guides"
      />
      <div className="max-w-3xl">
        <div className="overline text-[var(--cc-gold)] mb-2">Learn</div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold">
          Prize competition guides
        </h1>
        <p className="mt-4 text-[var(--cc-text-muted)] leading-relaxed">
          These guides explain the evidence behind Competition Checker&apos;s
          comparisons. They cover the practical questions that headline prize
          values miss: entry routes, ticket caps, odds, data confidence and the
          checks to make on the operator&apos;s live page.
        </p>
        <p className="mt-3 text-sm text-white/60 leading-relaxed">
          Published by {PUBLISHER_NAME}. This is independent information, not
          legal advice and not a recommendation to enter.
        </p>
      </div>

      <div className="mt-10 grid gap-5 md:grid-cols-2">
        {guides.map((guide) => <GuideCard key={guide.slug} guide={guide} />)}
      </div>

      <nav className="mt-12 flex flex-wrap gap-4 text-sm" aria-label="Related information">
        <Link className="text-[var(--cc-gold)] hover:underline" to="/how-we-score">
          Scoring methodology
        </Link>
        <Link className="text-[var(--cc-gold)] hover:underline" to="/data-accuracy">
          Data accuracy
        </Link>
        <Link className="text-[var(--cc-gold)] hover:underline" to="/responsible-participation">
          Responsible participation
        </Link>
      </nav>
    </div>
  );
}

function guideJsonLd(guide) {
  const canonical = `${SITE_URL}/guides/${guide.slug}`;
  const breadcrumbId = `${canonical}#breadcrumb`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "@id": `${canonical}#article`,
        headline: guide.title,
        description: guide.description,
        datePublished: guide.published,
        dateModified: guide.reviewed,
        mainEntityOfPage: { "@id": `${canonical}#webpage` },
        author: { "@type": "Organization", name: PUBLISHER_NAME },
        publisher: {
          "@type": "Organization",
          name: "QUOD-Tech Ltd",
          url: `${SITE_URL}/about`,
        },
      },
      {
        "@type": "WebPage",
        "@id": `${canonical}#webpage`,
        url: canonical,
        name: guide.title,
        description: guide.description,
        breadcrumb: { "@id": breadcrumbId },
      },
      {
        "@type": "BreadcrumbList",
        "@id": breadcrumbId,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Guides", item: `${SITE_URL}/guides` },
          { "@type": "ListItem", position: 3, name: guide.title, item: canonical },
        ],
      },
    ],
  };
}

export function GuideDetail() {
  const { slug } = useParams();
  const guide = guides.find((item) => item.slug === slug);

  if (!guide) {
    return (
      <main className="max-w-3xl mx-auto px-6 lg:px-8 py-20 text-center">
        <SEO
          title="Guide not found"
          description="The requested Competition Checker guide could not be found."
          path={`/guides/${slug || "missing"}`}
          noIndex
        />
        <div className="overline text-[var(--cc-gold)] mb-3">404</div>
        <h1 className="font-display text-3xl font-semibold">Guide not found</h1>
        <p className="mt-4 text-[var(--cc-text-muted)]">
          This guide may have moved or may not exist.
        </p>
        <Link className="btn-gold inline-flex mt-8 px-5 py-3 rounded" to="/guides">
          Browse guides
        </Link>
      </main>
    );
  }

  return (
    <article className="max-w-3xl mx-auto px-6 lg:px-8 py-12">
      <SEO
        title={guide.title}
        description={guide.description}
        path={`/guides/${guide.slug}`}
        ogType="article"
        jsonLd={guideJsonLd(guide)}
        jsonLdId={`guide-${guide.slug}`}
      />

      <nav aria-label="Breadcrumb" className="text-xs text-white/55">
        <Link className="hover:text-white" to="/">Home</Link>
        <span aria-hidden="true"> / </span>
        <Link className="hover:text-white" to="/guides">Guides</Link>
        <span aria-hidden="true"> / </span>
        <span>{guide.title}</span>
      </nav>

      <Link className="mt-6 inline-flex items-center gap-2 text-xs text-white/60 hover:text-white" to="/guides">
        <ArrowLeft size={14} /> All guides
      </Link>

      <header className="mt-7">
        <div className="overline text-[var(--cc-gold)] mb-2">Guide</div>
        <h1 className="font-display text-3xl sm:text-5xl font-semibold leading-tight">
          {guide.title}
        </h1>
        <p className="mt-5 text-lg leading-relaxed text-[var(--cc-text-muted)]">
          {guide.summary}
        </p>
        <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs text-white/50">
          <span>{guide.reading_time}</span>
          <span>Published {guide.published}</span>
          <span>Reviewed {guide.reviewed}</span>
          <span>{PUBLISHER_NAME}</span>
        </div>
      </header>

      <div className="mt-12 space-y-10">
        {guide.sections.map((section) => (
          <section key={section.heading}>
            <h2 className="font-display text-2xl font-semibold text-white">
              {section.heading}
            </h2>
            <div className="mt-4 space-y-4 text-[var(--cc-text-muted)] leading-relaxed">
              {section.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
              {section.bullets?.length > 0 && (
                <ul className="space-y-2 pl-5 list-disc marker:text-[var(--cc-gold)]">
                  {section.bullets.map((item) => <li key={item}>{item}</li>)}
                </ul>
              )}
              {section.links?.length > 0 && (
                <nav className="flex flex-col gap-2 pt-2" aria-label={`${section.heading} related links`}>
                  {section.links.map((link) => (
                    <Link key={link.href} className="text-[var(--cc-gold)] hover:underline" to={link.href}>
                      {link.label}
                    </Link>
                  ))}
                </nav>
              )}
            </div>
          </section>
        ))}
      </div>

      <aside className="mt-12 rounded-lg border border-white/10 bg-white/[0.03] p-6">
        <h2 className="font-display text-xl font-semibold">Use the live evidence</h2>
        <p className="mt-3 text-sm leading-relaxed text-[var(--cc-text-muted)]">
          Competition prices, availability, dates and terms can change. Use this
          guide to understand the comparison, then review the operator&apos;s
          official competition page and terms before entering.
        </p>
        <div className="mt-4 flex flex-wrap gap-4 text-sm">
          <Link className="text-[var(--cc-gold)] hover:underline" to="/competitions">Browse comparisons</Link>
          <Link className="text-[var(--cc-gold)] hover:underline" to="/how-we-score">How we score</Link>
          <Link className="text-[var(--cc-gold)] hover:underline" to="/responsible-participation">Responsible participation</Link>
        </div>
      </aside>
    </article>
  );
}
